import { FC, ReactNode, useEffect } from 'react';

import { Dialogs } from 'analytics/events/dialog';

import classNames from 'classnames';

import { ErrorWithUi, NoNetworkError } from 'errors';

import { observer } from 'mobx-react';

import { Helmet } from 'react-helmet';

import { useLocation } from 'react-router-dom';

import 'react-toastify/dist/ReactToastify.css';

import { useStores } from 'mobx/hooks/useStores';

import networkLabelService from 'services/networkLabelService';

import { API_URLS } from 'constants/apiUrls';

import 'containers/Layout/Layout.scss';

import { Backdrop } from 'components/Backdrop';
import { Footer } from 'components/Footer/Footer';
import Header from 'components/Header/Header';
import Loading from 'components/Loaders/Loading';
import { MessageDialog, DialogActionButtonProps } from 'components/UIkit/atoms/Dialog';
import { Toast } from 'components/UIkit/atoms/Toast/Toast';

const Layout: FC<{ children: ReactNode }> = ({ children }) => {
  const { uiStore, constantsStore } = useStores();
  const { error, errorDescription, clearError, isNotificationPanelOpen, cancelAction } = uiStore;
  const { pathname } = useLocation();
  const isWq = pathname.includes('work-queue');
  const errorTitle = error instanceof ErrorWithUi ? error.ui.title : error?.message;

  useEffect(() => {
    const handleConnectionAvailable = () => {
      if (Boolean(uiStore.error) && uiStore.error instanceof NoNetworkError) {
        uiStore.clearError();
      }
    };

    window.addEventListener('online', handleConnectionAvailable);

    return () => {
      window.removeEventListener('online', handleConnectionAvailable);
    };
  }, [uiStore]);

  const isLoading =
    !constantsStore.initialConstantsLoaded ||
    networkLabelService.isLoading(API_URLS.GENERATE_FAKE_REPORTS);

  const closeButtonProps = { onClick: clearError, text: cancelAction?.actionText || 'OK' };
  const primaryActionProps: DialogActionButtonProps = uiStore.action
    ? {
        onClick: uiStore.action.actionCallback,
        text: uiStore.action.actionText
      }
    : closeButtonProps;
  const secondaryActionProps = uiStore.additionalAction
    ? {
        onClick: uiStore.additionalAction.actionCallback,
        text: uiStore.additionalAction.actionText
      }
    : uiStore.action
    ? closeButtonProps
    : null;
  const tertiaryActionProps =
    uiStore.action && uiStore.additionalAction ? closeButtonProps : undefined;

  return (
    <div
      className={classNames('app', {
        'intro-mode': uiStore.isIntro,
        wq: isWq
      })}
      id="app"
    >
      <Helmet>
        <title>Canopy</title>
      </Helmet>
      <Header />

      <MessageDialog
        id={Dialogs.GeneralError}
        title={errorTitle}
        isOpen={!!error}
        handleClose={clearError}
        primaryActionProps={primaryActionProps}
        secondaryActionProps={secondaryActionProps}
        tertiaryActionProps={tertiaryActionProps}
      >
        {errorDescription?.map((str, index) => (
          <div key={index}>{str}</div>
        ))}
      </MessageDialog>

      <Toast />

      <div className="main">
        {isLoading ? (
          <Loading primaryColor />
        ) : (
          <>
            {children}
            <Backdrop isOpen={isNotificationPanelOpen} />
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default observer(Layout);
